import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import {
  Award,
  Coins,
  Crown,
  Layers,
  LayoutGrid,
  Sparkles,
  Ticket,
} from "lucide-react";

import { isAdmin } from "../../../redux/config";
import InnerLayout from "../../../components/layouts/inner-layout";
import { DataContext } from "../../../components/layouts/main-layout";
import successToast from "../../../components/helper-components/success-toast";
import errorToast from "../../../components/helper-components/error-toast";

// Loyalty actions + leaf tab components — lifted directly so we don't nest
// LoyaltyAdmin's own sub-tab strip inside ours.
import {
  loyaltyConfigGetAction,
  loyaltyConfigUpdateAction,
} from "../../../redux/actions/loyalty-action";
import EarningRulesTab from "../loyalty/tabs/earning-rules-tab";
import RedemptionTab from "../loyalty/tabs/redemption-tab";
import TiersTab from "../loyalty/tabs/tiers-tab";
import { FormBar } from "../loyalty/components/primitives";

// Coupons.
import {
  couponListAction,
  couponPauseAction,
  couponResumeAction,
  couponArchiveAction,
} from "../../../redux/actions/coupon-action";
import CouponListTab from "../coupons/tabs/list-tab";
import CouponCreateTab from "../coupons/tabs/create-tab";

// Schemes.
import {
  schemeListAction,
  schemeReorderAction,
  schemeCreateAction,
  schemeUpdateAction,
} from "../../../redux/actions/scheme-action";
import {
  schemeArchiveAction,
  schemePauseAction,
  schemeResumeAction,
} from "../../../redux/actions/scheme-action";
import SchemeListTab from "../schemes/tabs/list-tab";
import SchemeCreateTab from "../schemes/tabs/create-tab";

import PromotionsOverview from "./overview";

// One single flat tab strip — no nested sub-tabs. "create" is internal state,
// never a tab. We don't use window.location.hash because the inner Create
// components used to write `#create` and collide with the parent listener.
const TAB_DEFS = [
  { id: "overview", label: "Overview",         Icon: LayoutGrid },
  { id: "earn",     label: "Earning Rules",    Icon: Award },
  { id: "redeem",   label: "Redemption",       Icon: Coins },
  { id: "tiers",    label: "Membership Tiers", Icon: Crown },
  // Single Coupons tab covering both single coupons AND series — keeping
  // them split made the cashier hunt for a code by guessing which tab.
  // The list-tab already exposes filters (type, status) to slice the union.
  { id: "coupons",  label: "Coupons",          Icon: Ticket,   countKey: "coupons" },
  { id: "schemes",  label: "Schemes",          Icon: Layers,   countKey: "schemes" },
];

const isSeries = (c) =>
  !!(c?.series || c?.series_id || c?.config?.series || c?.discount_type === "series");

const PromotionsAdmin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { permissionFn } = useContext(DataContext);

  const [tab, setTab] = useState("overview");

  // ── Loyalty state ────────────────────────────────────────────────────
  const [loyaltyOriginal, setLoyaltyOriginal] = useState(null);
  const [loyaltyConfig, setLoyaltyConfig] = useState(null);
  const [loyaltySaving, setLoyaltySaving] = useState(false);

  // ── Coupon state ─────────────────────────────────────────────────────
  const [coupons, setCoupons] = useState([]);
  const [couponsLoading, setCouponsLoading] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);   // row to edit
  const [creatingCoupon, setCreatingCoupon] = useState(false); // bool: create panel open
  const [creatingSeries, setCreatingSeries] = useState(false);

  // ── Scheme state ─────────────────────────────────────────────────────
  const [schemes, setSchemes] = useState([]);
  const [schemesLoading, setSchemesLoading] = useState(false);
  const [editingScheme, setEditingScheme] = useState(null);
  const [creatingScheme, setCreatingScheme] = useState(false);
  const [schemeSaving, setSchemeSaving] = useState(false);

  const readOnly = !isAdmin;

  const permLoyalty = permissionFn ? permissionFn("loyalty_admin") || {} : { all: true };
  const permCoupon  = permissionFn ? permissionFn("coupons_admin") || {} : { all: true };
  const permScheme  = permissionFn ? permissionFn("schemes_admin") || {} : { all: true };
  const anyPerm = permLoyalty.all !== false || permCoupon.all !== false || permScheme.all !== false;

  // ── Loaders ──────────────────────────────────────────────────────────
  const loadLoyalty = () => {
    dispatch(
      loyaltyConfigGetAction(
        (res) => {
          const cfg = res?.data || {};
          setLoyaltyOriginal(JSON.parse(JSON.stringify(cfg)));
          setLoyaltyConfig(JSON.parse(JSON.stringify(cfg)));
        },
        () => errorToast("Failed to load loyalty config")
      )
    );
  };

  const loadCoupons = () => {
    setCouponsLoading(true);
    dispatch(
      couponListAction(
        {},
        (res) => {
          setCouponsLoading(false);
          // Backend envelope is { status, message, data: { rows, count } } —
          // the saga unwraps once so `res` is the body. Old code did
          // Array.isArray(res?.data) which always returned false here,
          // making the list silently empty.
          const rows = Array.isArray(res?.data)
            ? res.data
            : res?.data?.rows || [];
          setCoupons(rows);
        },
        (err) => {
          setCouponsLoading(false);
          setCoupons([]);
          if (err?.message) errorToast(err.message);
        }
      )
    );
  };

  const loadSchemes = () => {
    setSchemesLoading(true);
    dispatch(
      schemeListAction(
        {},
        (res) => {
          setSchemesLoading(false);
          const rows = Array.isArray(res?.data) ? res.data : res?.data?.rows || [];
          setSchemes(rows);
        },
        (err) => {
          setSchemesLoading(false);
          setSchemes([]);
          if (err?.message) errorToast(err.message);
        }
      )
    );
  };

  useEffect(() => {
    loadLoyalty();
    loadCoupons();
    loadSchemes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset transient creation state when switching tabs — otherwise the create
  // panel for one entity would still be open if the user toggles tabs.
  const goTo = (next) => {
    setTab(next);
    setCreatingCoupon(false);
    setCreatingSeries(false);
    setCreatingScheme(false);
    setEditingCoupon(null);
    setEditingScheme(null);
  };

  // ── Loyalty save ─────────────────────────────────────────────────────
  const loyaltyDirty =
    loyaltyOriginal && JSON.stringify(loyaltyOriginal) !== JSON.stringify(loyaltyConfig);

  const handleLoyaltySave = () => {
    if (!loyaltyConfig) return;
    setLoyaltySaving(true);
    const payload = {
      earn_config: loyaltyConfig.earn_config,
      redeem_config: loyaltyConfig.redeem_config,
      tiers: loyaltyConfig.tiers,
      category_multipliers: loyaltyConfig.category_multipliers || [],
    };
    dispatch(
      loyaltyConfigUpdateAction(
        payload,
        (res) => {
          setLoyaltySaving(false);
          const next = res?.data || loyaltyConfig;
          setLoyaltyOriginal(JSON.parse(JSON.stringify(next)));
          setLoyaltyConfig(JSON.parse(JSON.stringify(next)));
          successToast("Loyalty config saved");
        },
        (err) => {
          setLoyaltySaving(false);
          errorToast(err?.message || "Save failed");
        }
      )
    );
  };

  const handleLoyaltyCancel = () => {
    if (!loyaltyOriginal) return;
    if (loyaltyDirty && !window.confirm("Discard unsaved changes?")) return;
    setLoyaltyConfig(JSON.parse(JSON.stringify(loyaltyOriginal)));
  };

  // ── Scheme handlers ──────────────────────────────────────────────────
  const handleSchemeReorder = (order) => {
    if (readOnly) return;
    const byId = new Map(schemes.map((s) => [s.id, s]));
    const next = order
      .map(({ id, priority }) => ({ ...byId.get(id), priority }))
      .filter(Boolean);
    setSchemes(next);
    dispatch(
      schemeReorderAction(
        { order },
        () => successToast("Scheme priorities updated"),
        (err) => {
          errorToast(err?.message || "Reorder failed");
          loadSchemes();
        }
      )
    );
  };

  // Coupon lifecycle (pause / resume / archive) from the Promotions → Coupons
  // tab. Mirrors handleSchemeStatus + the standalone CouponsAdmin; without this
  // the list-tab buttons render but onStatusChange is undefined so clicks no-op.
  const handleCouponStatus = (id, nextStatus) => {
    if (readOnly) return;
    const actionMap = {
      paused: couponPauseAction,
      active: couponResumeAction,
      archived: couponArchiveAction,
    };
    const fn = actionMap[nextStatus];
    if (!fn) return;
    dispatch(
      fn(
        { id },
        () => { successToast(`Coupon ${nextStatus}`); loadCoupons(); },
        (err) => errorToast(err?.message || "Update failed")
      )
    );
  };

  const handleSchemeStatus = (id, nextStatus) => {
    if (readOnly) return;
    const actionMap = {
      paused: schemePauseAction,
      active: schemeResumeAction,
      archived: schemeArchiveAction,
    };
    const fn = actionMap[nextStatus];
    if (!fn) return;
    dispatch(
      fn(
        { id, body: {} },
        () => { successToast(`Scheme ${nextStatus}`); loadSchemes(); },
        (err) => errorToast(err?.message || "Update failed")
      )
    );
  };

  const handleSchemeSave = (payload) => {
    if (readOnly) return;
    setSchemeSaving(true);
    const isEdit = !!editingScheme?.id;
    const action = isEdit
      ? schemeUpdateAction({ id: editingScheme.id, body: payload },
          () => {
            setSchemeSaving(false);
            successToast("Scheme updated");
            setEditingScheme(null);
            setCreatingScheme(false);
            loadSchemes();
          },
          (err) => { setSchemeSaving(false); errorToast(err?.message || "Update failed"); })
      : schemeCreateAction(payload,
          () => {
            setSchemeSaving(false);
            successToast("Scheme created");
            setCreatingScheme(false);
            loadSchemes();
          },
          (err) => { setSchemeSaving(false); errorToast(err?.message || "Save failed"); });
    dispatch(action);
  };

  // ── Tab counts ───────────────────────────────────────────────────────
  const counts = useMemo(() => {
    const seriesRows = coupons.filter(isSeries);
    return {
      coupons: coupons.length - seriesRows.length,
      series:  seriesRows.length,
      schemes: schemes.length,
    };
  }, [coupons, schemes]);

  // ── Permission gate ──────────────────────────────────────────────────
  if (!anyPerm) {
    return (
      <InnerLayout title="Settings">
        <div className="h-full flex items-center justify-center text-[#64748B]">
          You don't have permission to view Promotions.
        </div>
      </InnerLayout>
    );
  }

  // ── Render the active tab ────────────────────────────────────────────
  const renderTab = () => {
    if (tab === "overview") {
      return (
        <PromotionsOverview
          loyaltyConfig={loyaltyOriginal}
          coupons={coupons}
          schemes={schemes}
          readOnly={readOnly}
          goTo={goTo}
          onNewCoupon={() => { setCreatingCoupon(true); setTab("coupons"); }}
        />
      );
    }
    if (!loyaltyConfig && ["earn", "redeem", "tiers"].includes(tab)) {
      return <div className="px-6 py-5 text-[13px] text-[#64748B]">Loading…</div>;
    }
    if (tab === "earn") {
      return (
        <div className="px-6 py-5 max-w-[1400px]">
          <EarningRulesTab config={loyaltyConfig} setConfig={setLoyaltyConfig} readOnly={readOnly} />
          {!readOnly && loyaltyDirty && (
            <FormBar hint="Changes apply to new transactions only."
              onCancel={handleLoyaltyCancel} onSave={handleLoyaltySave} saving={loyaltySaving} />
          )}
        </div>
      );
    }
    if (tab === "redeem") {
      return (
        <div className="px-6 py-5 max-w-[1400px]">
          <RedemptionTab config={loyaltyConfig} setConfig={setLoyaltyConfig} readOnly={readOnly} />
          {!readOnly && loyaltyDirty && (
            <FormBar hint="Changes apply to new transactions only."
              onCancel={handleLoyaltyCancel} onSave={handleLoyaltySave} saving={loyaltySaving} />
          )}
        </div>
      );
    }
    if (tab === "tiers") {
      return (
        <div className="px-6 py-5 max-w-[1400px]">
          <TiersTab config={loyaltyConfig} setConfig={setLoyaltyConfig} readOnly={readOnly} />
          {!readOnly && loyaltyDirty && (
            <FormBar hint="Changes apply to new transactions only."
              onCancel={handleLoyaltyCancel} onSave={handleLoyaltySave} saving={loyaltySaving} />
          )}
        </div>
      );
    }

    if (tab === "coupons") {
      if (creatingCoupon || editingCoupon) {
        return (
          <div className="px-6 py-5 max-w-[1400px]">
            <CouponCreateTab
              editing={editingCoupon}
              onCancel={() => { setEditingCoupon(null); setCreatingCoupon(false); }}
              onSaved={() => { setEditingCoupon(null); setCreatingCoupon(false); loadCoupons(); }}
            />
          </div>
        );
      }
      // Show ALL coupons — single + series — in one list. The list-tab's
      // built-in type/status filters slice the union without needing two tabs.
      return (
        <div className="px-6 py-5 max-w-[1400px]">
          <CouponListTab
            coupons={coupons}
            loading={couponsLoading}
            readOnly={readOnly}
            onCreate={() => setCreatingCoupon(true)}
            onEdit={(row) => setEditingCoupon(row)}
            onRefresh={loadCoupons}
            onStatusChange={handleCouponStatus}
          />
        </div>
      );
    }

    if (tab === "schemes") {
      if (creatingScheme || editingScheme) {
        return (
          <div className="px-6 py-5 max-w-[1400px]">
            <SchemeCreateTab
              readOnly={readOnly}
              saving={schemeSaving}
              editing={editingScheme}
              onCancel={() => { setEditingScheme(null); setCreatingScheme(false); }}
              onSave={handleSchemeSave}
            />
          </div>
        );
      }
      return (
        <div className="px-6 py-5 max-w-[1400px]">
          <SchemeListTab
            schemes={schemes}
            loading={schemesLoading}
            readOnly={readOnly}
            onReorder={handleSchemeReorder}
            onStatusChange={handleSchemeStatus}
            onEdit={(row) => setEditingScheme(row)}
            onCreate={() => setCreatingScheme(true)}
          />
        </div>
      );
    }

    return null;
  };

  return (
    <InnerLayout title="Settings">
      <div className="flex flex-col h-full bg-[#FAFBFC]">
        {/* Breadcrumb + title */}
        <div className="px-6 pt-4 pb-2 bg-white border-b border-[#EEF1F5]">
          <div className="flex items-center gap-2 text-[13px] text-[#475467]">
            <button className="flex items-center gap-1 hover:text-[#0F172A]"
              onClick={() => navigate("/settings")}>
              <ChevronLeftIcon className="h-4 w-4" /> Settings
            </button>
            <span>/</span>
            <span className="text-[#0F172A] font-medium">Loyalty &amp; Coupons</span>
            {readOnly && (
              <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#FFF3D6] text-[#9A6700]">
                Read-only at POS — edit in CPOS
              </span>
            )}
          </div>
          <h1 className="mt-1 text-[20px] font-bold text-[#0F172A]">Loyalty &amp; Coupons</h1>
        </div>

        {/* Flat tab strip */}
        <div className="px-6 bg-white border-b border-[#EEF1F5] overflow-x-auto">
          <div className="flex items-center gap-1">
            {TAB_DEFS.map((t) => {
              const Icon = t.Icon;
              const active = tab === t.id;
              const count = t.countKey ? counts[t.countKey] : null;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => goTo(t.id)}
                  className={`flex items-center gap-2 px-3.5 py-3 text-[13px] font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${
                    active
                      ? "border-primary text-primary"
                      : "border-transparent text-[#64748B] hover:text-[#334155]"
                  }`}
                >
                  <Icon size={14} />
                  {t.label}
                  {count != null && count > 0 && (
                    <span className={`inline-flex items-center justify-center text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${
                      active ? "bg-[#E0E7FF] text-primary" : "bg-[#F1F5F9] text-[#475569]"
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto">{renderTab()}</div>
      </div>
    </InnerLayout>
  );
};

export default PromotionsAdmin;
